/**
 * Перенос `_initialize` из `lib/main.dart`.
 *
 * В Dart это был `preInitialize` у `AppBuilder`: пока промис не завершился,
 * на экране крутился индикатор, а результат уходил в `MainApp` и оттуда —
 * в провайдеры. Здесь то же самое делает [bootstrapApp]: собирает
 * `AppInitialState`, а раскладывает его по хранилищам точка входа —
 * `appStateStore.getState().initAppState(...)` и
 * `languageStore.getState().initLanguage(...)`.
 *
 * Android-часть убрана: `KioskService.enableKiosk`,
 * `KioskService.setThisAppAsDefaultLauncher`, `SyncWakelockManager` и
 * `SystemChrome.setEnabledSystemUIMode(immersiveSticky)`.
 */

import { DEFAULT_BASE_URL, hasDefaultBaseUrl } from '@/config/defaults';
import type { AppInitialState } from '@/domain/models/appInitialState';
import { appLanguageTryFromString } from '@/domain/models/appLanguage';
import { initI18n } from '@/l10n/i18n';
import { fetchAppState } from '@/presentation/stores/appStateStore';
import { closeKeyboard } from '@/utils/appHelpers';
import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';
import { NetworkStatus } from '@/utils/networkStatus';
import { StorageHelper } from '@/utils/storageHelper';

/** `Duration(seconds: 5)` — пауза перед повторной попыткой старта. */
const RETRY_DELAY_MS = 5000;

/** `Future.delayed` — в JS ожидание собирается из `setTimeout`. */
function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
}

/**
 * Подставляет адрес планшета при самом первом запуске.
 *
 * Добавка к Dart-версии: там адрес всегда вводил администратор, и киоск
 * после установки упирался в экран адреса и PIN. Образ собирается под
 * конкретный планшет, поэтому адрес прописывается сразу — дальше он ничем
 * не отличается от введённого руками.
 *
 * Трогается только пустое хранилище: если адрес уже есть (администратор
 * сменил планшет), умолчание не вмешивается.
 */
async function seedDefaultBaseUrl(): Promise<void> {
  const storage = StorageHelper.instance;
  if (!hasDefaultBaseUrl() || storage.baseUrl !== null) return;

  await storage.setBaseUrl(DEFAULT_BASE_URL);
  DiagnosticsLog.instance.event(`адрес по умолчанию: ${DEFAULT_BASE_URL}`);
}

/**
 * Старт приложения. Возвращается только когда всё готово; при любом сбое
 * повторяется через [RETRY_DELAY_MS] — как в Dart.
 */
export async function bootstrapApp(): Promise<AppInitialState> {
  try {
    closeKeyboard();
    // Переводы. В Dart их поднимал делегат `AppLocalizations` внутри
    // `MaterialApp`, и к первому кадру они уже были. В вебе i18next надо
    // инициализировать явно — до первой записи в журнал и первого тоста,
    // иначе `getL10n()` бросит.
    await initI18n();
    await StorageHelper.instance.initialize();
    // Журнал для разработчика (5 тапов по версии + PIN). Поднимаем до
    // первых запросов, чтобы старт тоже в него попал.
    await DiagnosticsLog.instance.initialize();
    DiagnosticsLog.instance.event('запуск приложения');
    // До первого запроса конфигурации: без адреса он и не состоится.
    await seedDefaultBaseUrl();
    // Только для текста ошибок («нет сети» vs «сервер не отвечает»);
    // сбой канала запуск не останавливает.
    await NetworkStatus.instance.initialize();
    const appState = await fetchAppState(true);
    // Дословно как в Dart: у геттера тип `AppLanguage?`, и `toString()`
    // от `null` давал строку `null`, которую разбор не узнаёт и отдаёт
    // `null`. В JS ту же роль играет `String(...)`.
    const language = appLanguageTryFromString(
      String(StorageHelper.instance.language),
    );
    return {
      language: language,
      appState: appState,
    };
  } catch (e) {
    // Dart-версия молча ждала 5 секунд и вызывала себя заново: без
    // хранилища, сети или адреса киоску нечего показать, а человек у
    // экрана ничего не исправит. Поведение сохранено; причина уходит в
    // консоль — бесконечный повтор иначе выглядел бы зависанием.
    console.error('[bootstrap] запуск не удался, повтор через 5 с', e);
    await delay(RETRY_DELAY_MS);
    return bootstrapApp();
  }
}
